import { Table, TableSorting, LocalTableSorting } from "@/components/elements/Table";
import { useTable } from "@/hooks/useTable";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FCC } from "@/types/common";
import { Stack, Tooltip, Typography, IconButton, Menu, MenuItem } from "@mui/material";
import { EMPTY_STR } from "@/constants/string";
import { IconActionStar, IconNavigation } from "@/assets/icons";
import { findLabelByValue } from "@/utils/object";
import { useEnum } from "@/api/services/global/enum";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import { TTalentListOutputDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import dayjs from "dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { CaseTalentAnotherStatus } from "@/features/case/components/CaseMatches/CaseTalentAnotherStatus";
import { useParams } from "react-router-dom";
import { text } from "@/theme/colors";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { truncateWithEllipsis } from "@/utils/string";
import { downloadSingleResumeApi } from "@/api/services/main/talent";
import { showError } from "@/utils/error";
import { CenteredCell } from "@/components/commons/CenteredCell";
import { TalentAvatarCell } from "@/components/commons/TalentAvatarCell";

import { TCaseMatchDisplayColumnSetting } from "@/features/case/components/CaseMatches/case-match-filter-shared";

type TCaseMatchTableProps = {
  rows: TTalentListOutputDto[];
  page: number;
  isLoading?: boolean;
  onLocalSort?: (sortKey: string) => void;
  localSort?: string;
  storageKey: string;
  displayColumns?: TCaseMatchDisplayColumnSetting[];
  onSelectionChange?: (selectedIds: number[]) => void;
};

type ActionMenuCellProps = {
  row: TTalentListOutputDto;
};

const ActionMenuCell: React.FC<ActionMenuCellProps> = ({ row }) => {
  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  const menuVisible = useVisible(false);

  const handleMenuItemClick = async (action: string) => {
    menuVisible.close();
    if (action === "viewTalent" && row.id) {
      const detailTalentRoute = getDynamicRoute(AppRoutes.talentDetail, { talentId: String(row.id) });
      window.open(detailTalentRoute, "_blank");
    }
    if (action === "downloadResume" && row.id) {
      try {
        const response = await downloadSingleResumeApi(row.id);
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${row.fullName || "talent"}_resume.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        showError(error);
      }
    }
  };

  return (
    <>
      <IconButton ref={menuAnchorRef} onClick={menuVisible.open} size="small">
        <IconNavigation />
      </IconButton>
      <Menu
        anchorEl={menuAnchorRef.current}
        open={menuVisible.visible}
        onClose={menuVisible.close}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick("viewTalent")}>人材情報を確認</MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("downloadResume")}>履歴書をダウンロード</MenuItem>
      </Menu>
    </>
  );
};

export const CaseMatchTable: FCC<TCaseMatchTableProps> = ({
  rows,
  page,
  isLoading = false,
  onLocalSort,
  localSort,
  storageKey,
  displayColumns,
  onSelectionChange,
}) => {
  const { data: genderOptions } = useEnum("EGender");
  const { jobId } = useParams();
  const rowsRef = useRef(rows);
  const pageRef = useRef(page);
  const isSyncingSelection = useRef(false);

  const renderSortHeader = (name: string, label: string) => {
    if (onLocalSort) {
      return (
        <LocalTableSorting name={name} currentSort={localSort} onSort={onLocalSort}>
          {label}
        </LocalTableSorting>
      );
    }
    return <TableSorting name={name}>{label}</TableSorting>;
  };

  const columns = useMemo<MRT_ColumnDef<TTalentListOutputDto>[]>(
    () => [
      {
        id: "talentNumber",
        header: "人材管理番号",
        Header: renderSortHeader("talent.talentNumber", "人材管理番号"),
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.talentNumber ? truncateWithEllipsis(row.original.talentNumber, 20) : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        accessorKey: "talentNumber",
      },
      {
        id: "fullName",
        header: "名前",
        Header: renderSortHeader("talent.fullName", "名前"),
        Cell: ({ row }) => {
          const { caseMatches } = row.original;
          const isJoinCaseMatches = caseMatches.some((caseMatch) => Number(caseMatch.case?.jobId) === Number(jobId));
          return (
            <Stack direction="row" alignItems="center" gap={1}>
              <TalentAvatarCell filePath={row.original.avatarThumbnailPath} name={row.original.fullName} size={24} />
              {row.original.isRecommendedTalent && <IconActionStar />}
              {caseMatches && isJoinCaseMatches ? (
                <Tooltip title="同じ求人に過去提案歴がございます。詳細は人材情報をご確認ください。" placement="top-start">
                  <Typography variant="body2" color={text.error} sx={{ "&&": { p: 0 } }}>
                    {row.original?.fullName ? truncateWithEllipsis(row.original.fullName, 20) : EMPTY_STR.TEXT}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                  {row.original?.fullName ? truncateWithEllipsis(row.original.fullName, 20) : EMPTY_STR.TEXT}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        id: "nickName",
        header: "呼び名",
        Header: renderSortHeader("talent.nickName", "呼び名"),
        Cell: ({ row }) => (row.original.nickName ? truncateWithEllipsis(row.original.nickName, 20) : EMPTY_STR.TEXT),
      },
      {
        id: "isShowAnotherCase",
        header: "他提案",
        Cell: ({ row }) => {
          const { caseMatches } = row.original;
          let isAnotherStatus = false;
          if (caseMatches && caseMatches.length > 0) {
            isAnotherStatus = true;
          }
          return (
            <CenteredCell>
              <CaseTalentAnotherStatus isAnotherStatus={isAnotherStatus} />
            </CenteredCell>
          );
        },
      },
      {
        id: "nationalityName",
        accessorKey: "nationality.nationalityName",
        header: "国籍",
        Header: renderSortHeader("nationality.nationalityName", "国籍"),
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.nationality?.nationalityName || EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
      },
      {
        id: "gender",
        accessorKey: "gender",
        header: "性別",
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original?.gender),
      },
      {
        id: "age",
        header: "年齢",
        Cell: ({ row }) => {
          const {
            original: { birthday },
          } = row;
          if (!birthday) return EMPTY_STR.TEXT;
          const today = new Date();
          const birthDate = new Date(String(birthday));

          let age = today.getFullYear() - birthDate.getFullYear();

          const hasHadBirthdayThisYear =
            today.getMonth() > birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

          if (!hasHadBirthdayThisYear) {
            age--;
          }

          return age;
        },
      },
      {
        id: "residenceStatusId",
        header: "在留資格",
        Cell: ({ row }) => row.original?.residenceStatus?.residenceStatus || EMPTY_STR.TEXT,
        Header: renderSortHeader("residenceStatus.residenceStatus", "在留資格"),
      },
      {
        id: "periodOfStay",
        header: "在留期限",
        accessorKey: "periodOfStay",
        Cell: ({ row }) => {
          const {
            original: { periodOfStay },
          } = row;
          if (periodOfStay) {
            const periodDate = dayjs(periodOfStay);
            const now = dayjs();
            const monthsUntilExpiry = periodDate.diff(now, "month", true);
            const isExpiringSoon = monthsUntilExpiry <= 1;

            return (
              <Typography variant="body2" color={isExpiringSoon ? text.error : "inherit"} sx={{ "&&": { p: 0 } }}>
                {periodDate.format(DATE_FORMAT)}
              </Typography>
            );
          }
          return EMPTY_STR.DATE;
        },
        Header: renderSortHeader("talent.periodOfStay", "在留期限"),
      },
      {
        id: "languageCertificates",
        header: "日本語能力",
        Cell: ({ row }) => row.original.languageCertificates ?? EMPTY_STR.TEXT,
        Header: renderSortHeader("languageTestLevel.level", "日本語能力"),
      },
      {
        id: "partner",
        header: "パートナー",
        Cell: ({ row }) => (row.original?.partner?.partnerName ? truncateWithEllipsis(row.original?.partner?.partnerName, 20) : EMPTY_STR.TEXT),
      },
      {
        id: "personInChargeId",
        header: "担当者",
        Cell: ({ row }) =>
          row.original?.personInCharge?.fullName ? truncateWithEllipsis(row.original?.personInCharge.fullName, 20) : EMPTY_STR.TEXT,
        Header: renderSortHeader("personInCharge.fullName", "担当者"),
      },
      {
        id: "actions",
        header: "",
        Cell: ({ row }) => <ActionMenuCell row={row.original} />,
        size: 50,
        enablePinning: true,
      },
    ],
    [genderOptions, onLocalSort, localSort],
  );

  const getCaseMatchSelected = (): number[] => {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      return Object.values(parsed).flat() as number[];
    } catch {
      return [];
    }
  };

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    const ids = getCaseMatchSelected();
    setSelectedIds(ids);

    const expectedSelection: MRT_RowSelectionState = {};
    rows.forEach((row) => {
      if (ids.includes(row.id)) {
        expectedSelection[row.id.toString()] = true;
      }
    });
    table.setRowSelection(expectedSelection);
  }, [storageKey]);

  const currentPageSelectedIds = useMemo(() => {
    const currentRowIds = rows.map((r) => r.id);
    return selectedIds.filter((id: number) => currentRowIds.includes(id));
  }, [rows, selectedIds]);

  const { table } = useTable({
    columns,
    rows,
    enableRowSelection: true,
    enableSelectAll: false,
    getRowId: (row) => row.id.toString(),
    pinningColumns: ["actions"],
    displayColumnDefOptions: {
      "mrt-row-select": {
        header: "",
      },
    },
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    state: {},
  });

  // Apply display column settings (order + visibility) via MRT API
  useEffect(() => {
    if (!displayColumns) return;

    const orderedIds = displayColumns.filter((s) => s.visible).map((s) => s.id);
    // Keep checkbox column first, actions column last (pinned right)
    table.setColumnOrder(["mrt-row-select", ...orderedIds, "actions"]);

    const visibilityMap: Record<string, boolean> = {};
    displayColumns.forEach((s) => {
      visibilityMap[s.id] = s.visible;
    });
    // Actions column always visible
    visibilityMap["actions"] = true;
    table.setColumnVisibility(visibilityMap);
  }, [displayColumns, table]);

  const ids = React.useMemo(() => table.getSelectedRowModel().rows.map((row) => row.original.id), [table.getSelectedRowModel()]);

  useEffect(() => {
    const prevRowIds = rowsRef.current.map((r) => r.id).sort((a, b) => a - b);
    const currentRowIds = rows.map((r) => r.id).sort((a, b) => a - b);
    const rowsChanged = prevRowIds.length !== currentRowIds.length || prevRowIds.some((id, idx) => id !== currentRowIds[idx]);
    const pageChanged = pageRef.current !== page;

    if (rowsChanged || pageChanged) {
      rowsRef.current = rows;
      pageRef.current = page;
      isSyncingSelection.current = true;

      const expectedSelection: MRT_RowSelectionState = {};
      currentPageSelectedIds.forEach((id: number) => {
        expectedSelection[id.toString()] = true;
      });
      table.setRowSelection(expectedSelection);

      isSyncingSelection.current = false;
    }
  }, [rows, page, currentPageSelectedIds, table]);

  useEffect(() => {
    if (isSyncingSelection.current) return;

    const currentRowIds = rows.map((r) => r.id);
    const newSelectedIds = ids;
    const deselectedIds = currentRowIds.filter((id) => !newSelectedIds.includes(id));

    setSelectedIds((prev: number[]) => {
      const filteredPrev = prev.filter((id: number) => !deselectedIds.includes(id));
      const merged = Array.from(new Set([...filteredPrev, ...newSelectedIds]));

      sessionStorage.setItem(storageKey, JSON.stringify(merged));
      onSelectionChange?.(merged);

      return merged;
    });
  }, [ids, rows, onSelectionChange]);

  return <Table table={table} isLoading={isLoading} maxHeight={440} headRowHeight={40} bodyRowHeight={42} />;
};
